import { useEffect, useMemo, useState } from "react";

import apiClient from "../../../services/apiClient";
import Modal from "../../../components/Modal";
import CloseButton from "../../../components/CloseButton";
import "./VocabularyAnswerModal.css";

// 단어 시험 답 보기 · 채점 다시(2026-11-05). 선생님 화면에만 있다. 서버: api/assignment/vocabulary_answer_views.py
// 모양은 학생 수학 '빠른 정답 입력' 과 같은 톤. 표 하나에 한 문항 한 줄, 채점 칸의 동그라미를 누르면 ○ ↔ ✕.
// 카드의 '학생 답' 은 아이가 쓴 글 그대로다(서버가 시험 답의 input 을 손대지 않고 내려준다).
// '채점 다시' 는 틀린 뜻 쓰기 문항만 한 문항씩 AI 에게 다시 묻는다(맞은 것 · 선생님이 바꾼 것은 건드리지 않는다).

const REGRADE_CONCURRENCY = 3;

const AnswerRow = ({ number, item, isBusy, onMark }) => (
  <tr className={item.is_correct ? "" : "voca-answer__row--wrong"}>
    <td className="voca-answer__no">{number}</td>
    <td>
      <span className="voca-answer__word">{item.word}</span>
      {item.prompt && item.prompt !== item.word && (
        <span className="voca-answer__prompt" title={item.prompt}>{item.prompt}</span>
      )}
      <span className="voca-answer__answer voca-answer__answer-under">{item.answer}</span>
    </td>
    <td className="voca-answer__cell-answer">
      <span className="voca-answer__answer">{item.answer}</span>
    </td>
    <td>
      {item.input ? <span className="voca-answer__input">{item.input}</span> : <span className="voca-answer__empty">(안 씀)</span>}
      {item.is_teacher_marked && <span className="voca-answer__teacher">선생님 채점</span>}
    </td>
    <td className="voca-answer__cell-mark">
      <button
        type="button"
        disabled={isBusy}
        title={item.is_correct ? "누르면 오답으로" : "누르면 정답으로"}
        onClick={() => onMark(item, !item.is_correct)}
        className={`voca-answer__mark voca-answer__mark--${item.is_correct ? "right" : "wrong"}`}
      >
        {item.is_correct ? "○" : "✕"}
      </button>
    </td>
  </tr>
);

const VocabularyAnswerModal = ({ assignment, onClose, onGradeChange }) => {
  const [data, setData] = useState(null);
  const [isWrongOnly, setIsWrongOnly] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [regradeProgress, setRegradeProgress] = useState(null);

  const keyOf = (item) => `${item.section_id}-${item.index}`;

  const load = () =>
    apiClient
      .get("/api/v1/vocabulary/answer/list", { params: { assignment_id: assignment.id }, withAuth: true })
      .then((response) => {
        setData(response.data);
        return response.data;
      })
      .catch(() => setData({ error: true }));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment.id]);

  const applyResult = (result) => {
    setData((previous) => ({
      ...previous,
      grade: result.grade,
      items: previous.items.map((one) => (keyOf(one) === keyOf(result.item) ? result.item : one)),
    }));
    onGradeChange?.(assignment.id, result.grade);
  };

  const mark = async (item, isCorrect) => {
    if (item.is_correct === isCorrect) return;
    setSavingKey(keyOf(item));
    try {
      const response = await apiClient.post(
        "/api/v1/vocabulary/answer/mark",
        { assignment_id: assignment.id, section_id: item.section_id, index: item.index, is_correct: isCorrect },
        { withAuth: true }
      );
      applyResult(response.data);
    } catch (error) {
      alert(error?.response?.data?.error || "저장하지 못했어요.");
    } finally {
      setSavingKey("");
    }
  };

  const regradeTargetList = useMemo(
    () => (data?.items || []).filter((one) => one.can_regrade && !one.is_correct && !one.is_teacher_marked && one.input),
    [data]
  );

  const regrade = async () => {
    const targetList = [...regradeTargetList];
    const total = targetList.length;
    let doneCount = 0;
    let fixedCount = 0;
    setRegradeProgress({ done: 0, total, fixed: 0 });
    const worker = async () => {
      while (targetList.length) {
        const item = targetList.shift();
        try {
          const response = await apiClient.post(
            "/api/v1/vocabulary/answer/regrade",
            { assignment_id: assignment.id, section_id: item.section_id, index: item.index },
            { withAuth: true }
          );
          if (response.data.item.is_correct) fixedCount += 1;
          applyResult(response.data);
        } catch (error) {
          // 한 문항이 실패해도 나머지는 계속 묻는다
        }
        doneCount += 1;
        setRegradeProgress({ done: doneCount, total, fixed: fixedCount });
      }
    };
    await Promise.all(Array.from({ length: REGRADE_CONCURRENCY }, worker));
    // 여러 문항을 함께 보내서 응답마다 실린 점수는 순서가 섞일 수 있다. 끝나면 한 번 다시 받는다
    const latest = await load();
    if (latest?.grade !== undefined) onGradeChange?.(assignment.id, latest.grade);
    setRegradeProgress({ done: doneCount, total, fixed: fixedCount, isFinished: true });
  };

  const itemList = data?.items || [];
  const wrongCount = itemList.filter((one) => !one.is_correct).length;
  const shownList = itemList
    .map((item, index) => ({ item, number: index + 1 }))
    .filter(({ item }) => !isWrongOnly || !item.is_correct);
  const isRegrading = Boolean(regradeProgress && !regradeProgress.isFinished);
  const isLoaded = data && !data.error;

  return (
    <Modal>
      <div className="voca-answer">
        <CloseButton onClick={onClose} />
        <div className="voca-answer__top">
          <div className="voca-answer__title">
            {data?.student_name || assignment.student?.name}
            <span className="voca-answer__chip">답 보기</span>
          </div>
          {isLoaded && (
            <div className={`voca-answer__score${data.grade < 50 ? " voca-answer__score--low" : ""}`}>
              {data.grade}점 · {itemList.length - wrongCount} / {itemList.length}
            </div>
          )}
          {isLoaded && (
            <div className="voca-answer__filter">
              <button
                type="button"
                className={`voca-answer__filter-button${isWrongOnly ? "" : " voca-answer__filter-button--on"}`}
                onClick={() => setIsWrongOnly(false)}
              >
                전체 {itemList.length}
              </button>
              <button
                type="button"
                className={`voca-answer__filter-button${isWrongOnly ? " voca-answer__filter-button--on" : ""}`}
                onClick={() => setIsWrongOnly(true)}
              >
                틀린 것 {wrongCount}
              </button>
            </div>
          )}
        </div>
        <div className="voca-answer__body">
          {!data && <div className="voca-answer__message">불러오는 중입니다.</div>}
          {data?.error && <div className="voca-answer__message">불러오지 못했습니다.</div>}
          {isLoaded && shownList.length === 0 && <div className="voca-answer__message">틀린 문항이 없어요.</div>}
          {isLoaded && shownList.length > 0 && (
            <table className="voca-answer__table">
              <thead>
                <tr>
                  <th className="voca-answer__col-no"></th>
                  <th>문제</th>
                  <th className="voca-answer__col-answer">정답</th>
                  <th>학생 답</th>
                  <th className="voca-answer__col-mark">채점</th>
                </tr>
              </thead>
              <tbody>
                {shownList.map(({ item, number }) => (
                  <AnswerRow key={keyOf(item)} number={number} item={item} isBusy={savingKey === keyOf(item) || isRegrading} onMark={mark} />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {isLoaded && (
          <div className="voca-answer__actions">
            {regradeProgress && (
              <span className="voca-answer__progress">
                {regradeProgress.isFinished
                  ? `다시 채점 끝 · ${regradeProgress.fixed}개 정답으로`
                  : `다시 채점 중 ${regradeProgress.done} / ${regradeProgress.total}`}
              </span>
            )}
            <button type="button" className="voca-answer__submit voca-answer__submit--ghost" onClick={onClose}>
              닫기
            </button>
            <button
              type="button"
              className="voca-answer__submit"
              disabled={isRegrading || regradeTargetList.length === 0}
              onClick={regrade}
            >
              채점 다시{regradeTargetList.length ? ` ${regradeTargetList.length}` : ""}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VocabularyAnswerModal;
